import type { JobTag } from '@seek/chalice-types';

import type { GetCareerFeedQuery } from 'src/graphql/graphql';

import type { Job } from '../SharedComponent/JobCard/JobCard';

// Graph Types
type CareerFeed = NonNullable<NonNullable<GetCareerFeedQuery['careerFeed']>>;
type GraphFeedEdge = NonNullable<CareerFeed['edges']>[number];
type GraphFeedItem = NonNullable<GraphFeedEdge>['node'];
type GraphFeedJob = Extract<GraphFeedItem, { __typename: 'CareerFeedJob' }>;

const NODES_BEFORE_SURVEY_LIMIT = 20;

// Local Types
type CareerFeedJob = Job & {
  type: 'JOB';
};
type CareerFeedRecsInterceptSurvey = {
  type: 'RECS_INTERCEPT_SURVEY';
};
export type CareerFeedItem = CareerFeedJob | CareerFeedRecsInterceptSurvey;

// Local predicates
export const isCareerFeedJob = (item: CareerFeedItem): item is CareerFeedJob =>
  item.type === 'JOB';
export const isCareerFeedRecsInterceptSurvey = (
  item: CareerFeedItem,
): item is CareerFeedRecsInterceptSurvey =>
  item.type === 'RECS_INTERCEPT_SURVEY';

// Mappers from GraphQL to local types
export const mapJobBadgesToJobTags = (
  jobBadges: GraphFeedJob['jobBadges'],
): JobTag[] =>
  (jobBadges?.badgeDetails || [])
    .map((badge) => ({
      label: badge?.displayText,
      type: badge?.badge,
    }))
    .filter(
      (badge): badge is JobTag => Boolean(badge.label) && Boolean(badge.type),
    );
const mapToCareerFeedJob = (
  feedJob: GraphFeedJob,
): CareerFeedJob | undefined => {
  if (!feedJob.job) {
    return;
  }

  const {
    job: { advertiser, id, listedAt, location, products, salary, title },
    solMetadata,
    savedJob,
    jobBadges,
  } = feedJob;

  return {
    type: 'JOB',
    advertiserName: advertiser.name,
    id,
    isSaved: Boolean(savedJob),
    listingDateLabel: listedAt?.label,
    locationLabel: location.label,
    salaryCurrencyLabel: salary?.currencyLabel ?? undefined,
    salaryLabel: salary?.label,
    solMetadata,
    srcLogo: products?.branding?.logo?.url,
    tags: mapJobBadgesToJobTags(jobBadges),
    title,
    bullets: products?.bullets ?? undefined,
  };
};

const mapEdgeToCareerFeedItem = (
  feedItem: GraphFeedEdge,
): CareerFeedItem | undefined => {
  switch (feedItem?.node?.__typename) {
    case 'CareerFeedJob':
      return mapToCareerFeedJob(feedItem.node);
    default:
      return undefined;
  }
};

type MapCareerFeedItemOptions = {
  limitBeforeSurvey: number;
};

export const mapQueryToCareerFeedItem = (
  careerFeedQueryResponse: GetCareerFeedQuery | undefined,
  { limitBeforeSurvey }: MapCareerFeedItemOptions = {
    limitBeforeSurvey: NODES_BEFORE_SURVEY_LIMIT,
  },
): CareerFeedItem[] | undefined => {
  const feedItems = careerFeedQueryResponse?.careerFeed?.edges
    ?.map<CareerFeedItem | undefined>(mapEdgeToCareerFeedItem)
    .filter((item): item is CareerFeedItem => Boolean(item));

  // Temporarily adding the survey in the mapping layer.
  // Ideally, this should come from the backend.
  if (feedItems && feedItems.length >= limitBeforeSurvey) {
    feedItems.splice(limitBeforeSurvey, 0, {
      type: 'RECS_INTERCEPT_SURVEY',
    });
  }

  return feedItems;
};
